import { ref, reactive, computed } from 'vue'
import { useErpI18n } from '@/composables/useErpI18n'

interface PaperType {
  width: number
  height: number
}

interface CurPaper {
  type: string
  width: number
  height: number
}

interface PaperFormData {
  name: string
  paperType: string
  customWidth: number
  customHeight: number
}

export function usePaperManagement() {
  // 使用多语言
  const { t } = useErpI18n()

  // 表单数据
  const form = reactive<PaperFormData>({
    name: '',
    paperType: 'A4',
    customWidth: 210,
    customHeight: 296.6,
  })

  // 当前纸张
  const curPaper = reactive<CurPaper>({
    type: 'A4',
    width: 210,
    height: 296.6,
  })

  // 纸张类型 - 使用计算属性来支持响应式翻译
  const paperTypes = computed(() => [
    {
      label: t('designer.paperTypes.custom'),
      value: 'custom',
      paperInfo: {
        width: 210,
        height: 296.6,
      },
    },
    {
      label: 'A3',
      value: 'A3',
      paperInfo: {
        width: 420,
        height: 296.6,
      },
    },
    {
      label: 'A4',
      value: 'A4',
      paperInfo: {
        width: 210,
        height: 296.6,
      },
    },
    {
      label: 'A5',
      value: 'A5',
      paperInfo: {
        width: 210,
        height: 147.6,
      },
    },
    {
      label: 'B3',
      value: 'B3',
      paperInfo: {
        width: 500,
        height: 352.6,
      },
    },
    {
      label: 'B4',
      value: 'B4',
      paperInfo: {
        width: 250,
        height: 352.6,
      },
    },
    {
      label: 'B5',
      value: 'B5',
      paperInfo: {
        width: 250,
        height: 175.6,
      },
    },
  ])

  // 自定义纸张
  const paperPopVisible = ref(false)
  const paperWidth = ref('220')
  const paperHeight = ref('80')

  // 计算当前纸张类型
  const curPaperType = computed(() => {
    let type = 'other'
    for (const paperType of paperTypes.value) {
      const { width, height } = curPaper
      if (paperType.paperInfo.width === width && paperType.paperInfo.height === height) {
        type = paperType.value
        break
      }
    }
    return type
  })

  // 处理纸张类型改变
  const handlePaperTypeChange = (value: string, printInstance: any) => {
    if (value === 'custom') return
    const paperInfo = paperTypes.value.find((item) => item.value === value)?.paperInfo
    if (paperInfo) {
      form.customWidth = paperInfo.width
      form.customHeight = paperInfo.height
      setPaper(value, paperInfo, printInstance)
    }
  }

  // 设置纸张大小
  const setPaper = (type: string, value: PaperType, printInstance: any) => {
    const isPredefinedType = paperTypes.value.some((p) => p.value === type)

    if (isPredefinedType) {
      curPaper.type = type
      curPaper.width = value.width
      curPaper.height = value.height
    } else {
      curPaper.type = 'other'
      curPaper.width = value.width
      curPaper.height = value.height
    }

    if (printInstance) {
      printInstance.setPaper(value.width, value.height)
    }
  }

  // 自定义纸张
  const otherPaper = (printInstance: any) => {
    const custom = paperTypes.value.find((item) => item.value === 'custom')
    if (custom && form.customWidth && form.customHeight) {
      custom.paperInfo = {
        width: Number(form.customWidth),
        height: Number(form.customHeight),
      }
      setPaper('other', custom.paperInfo, printInstance)
    }
  }

  // 旋转纸张
  const rotatePaper = (printInstance: any) => {
    const newWidth = curPaper.height
    const newHeight = curPaper.width

    curPaper.width = newWidth
    curPaper.height = newHeight

    if (printInstance) {
      printInstance.setPaper(newWidth, newHeight)
    }
  }

  return {
    form,
    curPaper,
    paperTypes,
    paperPopVisible,
    paperWidth,
    paperHeight,
    curPaperType,
    handlePaperTypeChange,
    setPaper,
    otherPaper,
    rotatePaper,
  }
}
